import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { NotificationTypeEnum, HeaderDialogsEnum } from '@/types';
import { authSetIsLoggedIn, appSetNotification } from '@/store';
import { Loader, MyDialog, Pagination } from '@/components';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { adminProvider, chatProvider, sessionProvider } from '@/providers';

import { PaginationType } from '@/types';
import {
  UserMenu,
  UserTable,
  VerificationRequestsTable,
  UserSearch,
  UnregisteredTable,
  OtpTable,
} from './UserComponents';
import BlockUser from '../Dialogs/BlockUser';
import UnBlockUser from '../Dialogs/UnBlockUser';
import BlockHistory from '../Dialogs/BlockHistory';
import ImpersonateUser from '../Dialogs/ImpersonateUser';
import { AmbassadorFilterValue, PartiallyRegisteredUser, User } from './types/types';
import EmploymentRequest from '../Dialogs/EmploymentRequest';
import ForeignBrokerVerification from '../Dialogs/ForeignBrokerVerification';
import { useChangeQueryParams } from '@/hooks';

import { Box, Button, DialogActions, Stack, Tabs, Tab, Typography, styled } from '@mui/material';

import { useDialogs } from '@/components/Dashboard/DialogsContext';

const StyledTabs = styled(Tabs)`
  .MuiTabs-indicator {
    background-color: #2a3842;
  }

  .MuiTabs-flexContainer {
    justify-content: flex-start;
  }

  margin-bottom: 20px;
`;

const StyledTab = styled(Tab)`
  color: #2a3842;
  font-size: 20px;
  font-family: 'Poppins';
  font-weight: 600;
  line-height: 24px;
  opacity: 0.5;
  text-transform: none;

  &.Mui-selected {
    opacity: 1;
    color: #2a3842;
  }
`;

export const Users = (): JSX.Element => {
  const dispatch = useDispatch();
  const { setOpenDialog } = useDialogs();

  const [isLoading, setIsLoading] = useState<boolean | null>(true);
  const [limit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);

  const [users, setUsers] = useState<User[]>([]);
  const [userTotal, setUserTotal] = useState<number>(0);

  const [anchorEl, setAnchorEl] = useState<Element | undefined>();
  const [selectedUserId, setSelectedUserId] = useState<number>();

  const changeQueryParams = useChangeQueryParams('admin/users');
  const [paginationObj, setPaginationObj] = useState<PaginationType>();
  const { search: queryParamsStr } = useLocation();
  const queryParams = queryString.parse(queryParamsStr);

  const [search, setSearch] = useState<string>('');
  const [updateOtpTable, setUpdateOtpTable] = useState(false);

  const [type, setType] = useState<string>('all');
  const [ambassadorFilter, setAmbassadorFilter] = useState<AmbassadorFilterValue>(
    AmbassadorFilterValue.All
  );

  const isAmbassadorFilter =
    ambassadorFilter === AmbassadorFilterValue.All
      ? undefined
      : ambassadorFilter === AmbassadorFilterValue.Ambassadors;

  const [openBlockUserDialog, setOpenBlockUserDialog] = useState<boolean>(false);
  const [openUnBlockUserDialog, setOpenUnBlockUserDialog] = useState<boolean>(false);
  const [openBlockHistoryDialog, setOpenBlockHistoryDialog] = useState<boolean>(false);
  const [openImpersonateUserDialog, setOpenImpersonateUserDialog] = useState<boolean>(false);
  const [openEmploymentRequestDialog, setOpenEmploymentRequestDialog] = useState<boolean>(false);
  const [openForeignBrokerVerificationDialog, setOpenForeignBrokerVerificationDialog] =
    useState<boolean>(false);

  const [tabValue, setTabValue] = useState<number>(0);

  // non-verified users
  const [nonVerifiedUsers, setNonVerifiedUsers] = useState<User[]>([]);
  const [nonVerifiedUserTotal, setNonVerifiedUserTotal] = useState<number>(0);
  const [nonVerifiedUsersPaginationObj, setNonVerifiedUsersPaginationObj] =
    useState<PaginationType>();

  // unregistred users
  const [unregistredUsers, setUnregistredUsers] = useState<PartiallyRegisteredUser[]>([]);
  const [unregistredUsersTotal, setUnregistredUsersTotal] = useState<number>(0);
  const [unregistredUsersPaginationObj, setUnregistredUsersPaginationObj] =
    useState<PaginationType>();
  const [unregisteredUserToDelete, setUnregisteredUserToDelete] =
    useState<PartiallyRegisteredUser | null>(null);
  const [isDeletingUnregisteredUser, setIsDeletingUnregisteredUser] = useState(false);
  const deleteUnregisteredUserInFlightRef = useRef(false);

  const handleMenuClick = (event: React.MouseEvent<Element>, userId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
    setAnchorPosition({ top: event.clientY, left: event.clientX });
  };

  const handleMenuClose = () => {
    setAnchorEl(undefined);
    setAnchorPosition(undefined);
  };

  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | undefined>();

  const userListRequestIdRef = useRef(0);
  const nonVerifiedUserListRequestIdRef = useRef(0);
  const unregisteredUserListRequestIdRef = useRef(0);
  const loadingRequestIdRef = useRef(0);
  const isInitialUserListLoadRef = useRef(true);

  const handleUserListError = (status?: number, message?: string) => {
    if (status === 401) {
      dispatch(authSetIsLoggedIn(false));
      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          'Your access token is expired, please login again'
        )
      );
      return;
    }

    dispatch(
      appSetNotification(NotificationTypeEnum.Error, message || 'Error when loading users', status)
    );
  };

  const getUserList = async (newLimit?: number, newOffset?: number) => {
    const requestId = ++userListRequestIdRef.current;
    const actualLimit = newLimit ?? limit;
    const actualOffset = newOffset ?? offset;
    const { ok, data, status, message } = await adminProvider.getUsers(
      search,
      actualLimit,
      actualOffset,
      type,
      undefined,
      isAmbassadorFilter
    );

    if (requestId !== userListRequestIdRef.current) {
      return null;
    }

    if (ok && data) {
      setUsers(data.items);
      setUserTotal(data.total);

      setPaginationObj({
        page: Math.floor(actualOffset / actualLimit) + 1,
        offset: actualOffset,
        limit: actualLimit,
        total: data.total,
      });
      return data;
    } else {
      handleUserListError(status, message);
      return null;
    }
  };

  const getNonVerifiedUserList = async (newLimit?: number, newOffset?: number) => {
    const requestId = ++nonVerifiedUserListRequestIdRef.current;
    const actualLimit = newLimit ?? limit;
    const actualOffset = newOffset ?? offset;
    const { ok, data, status, message } = await adminProvider.getUsers(
      search,
      actualLimit,
      actualOffset,
      type,
      false,
      isAmbassadorFilter
    );

    if (requestId !== nonVerifiedUserListRequestIdRef.current) {
      return null;
    }

    if (ok && data) {
      setNonVerifiedUsers(data.items);
      setNonVerifiedUserTotal(data.total);

      setNonVerifiedUsersPaginationObj({
        page: Math.floor(actualOffset / actualLimit) + 1,
        offset: actualOffset,
        limit: actualLimit,
        total: data.total,
      });
      return data;
    } else {
      handleUserListError(status, message);
      return null;
    }
  };

  const getUnregisteredUserList = async (newLimit?: number, newOffset?: number) => {
    const requestId = ++unregisteredUserListRequestIdRef.current;
    const actualLimit = newLimit ?? limit;
    const actualOffset = newOffset ?? offset;
    const { ok, data, status, message } = await adminProvider.unregisteredUser(
      actualLimit,
      actualOffset
    );

    if (requestId !== unregisteredUserListRequestIdRef.current) {
      return null;
    }

    if (ok && data) {
      setUnregistredUsers(data.items);
      setUnregistredUsersTotal(data.total);

      setUnregistredUsersPaginationObj({
        page: Math.floor(actualOffset / actualLimit) + 1,
        offset: actualOffset,
        limit: actualLimit,
        total: data.total,
      });
      return data;
    } else {
      handleUserListError(status, message);
      return null;
    }
  };

  const closeDeleteUnregisteredUserDialog = () => {
    if (!deleteUnregisteredUserInFlightRef.current) setUnregisteredUserToDelete(null);
  };

  const handleDeleteUnregisteredUser = async () => {
    if (!unregisteredUserToDelete || deleteUnregisteredUserInFlightRef.current) return;

    deleteUnregisteredUserInFlightRef.current = true;
    setIsDeletingUnregisteredUser(true);
    const targetId = unregisteredUserToDelete.id;
    let deleted = false;

    try {
      const { ok, message, status } = await adminProvider.deletePartiallyRegisteredUser(targetId);
      if (!ok) {
        dispatch(
          appSetNotification(
            NotificationTypeEnum.Error,
            message || 'Unable to delete unregistered user',
            status
          )
        );
        return;
      }

      deleted = true;
      setUnregisteredUserToDelete(null);
      dispatch(appSetNotification(NotificationTypeEnum.Success, 'Unregistered user deleted.'));

      const result = await getUnregisteredUserList(limit, offset);
      if (result && result.items.length === 0 && offset > 0) {
        const previousAvailablePage = Math.max(
          1,
          Math.min(Math.floor(offset / limit), Math.ceil(result.total / limit))
        );
        const nextOffset = (previousAvailablePage - 1) * limit;
        setOffset(nextOffset);
        changeQueryParams({ page: previousAvailablePage });
        await getUnregisteredUserList(limit, nextOffset);
      }
    } catch (error) {
      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          deleted
            ? 'Unregistered user deleted, but the list could not be refreshed.'
            : error instanceof Error
              ? error.message
              : 'Unable to delete unregistered user'
        )
      );
    } finally {
      deleteUnregisteredUserInFlightRef.current = false;
      setIsDeletingUnregisteredUser(false);
    }
  };

  const handleChangePage = async (page: number) => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    const loadingRequestId = ++loadingRequestIdRef.current;
    setIsLoading(true);
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);
    changeQueryParams({ page: page });

    try {
      switch (tabValue) {
        case 0:
          await getUserList(limit, newOffset);
          break;
        case 1:
          await getNonVerifiedUserList(limit, newOffset);
          break;
        case 2:
          await getUnregisteredUserList(limit, newOffset);
          break;
      }
    } finally {
      if (loadingRequestId === loadingRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleBanUser = async (reason: string) => {
    const userId = selectedUserId;
    setOpenBlockUserDialog(false);
    const loadingRequestId = ++loadingRequestIdRef.current;
    setIsLoading(true);

    try {
      const { ok } = await adminProvider.banUser(userId!, reason);
      if (ok) {
        await getUserList();
      } else {
        dispatch(appSetNotification(NotificationTypeEnum.Error, 'error when ban user'));
      }
    } finally {
      if (loadingRequestId === loadingRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleUnBanUser = async () => {
    const userId = selectedUserId;
    setOpenUnBlockUserDialog(false);
    const loadingRequestId = ++loadingRequestIdRef.current;
    setIsLoading(true);

    try {
      const { ok } = await adminProvider.unBanUser(userId!);
      if (ok) {
        await getUserList();
      } else {
        dispatch(appSetNotification(NotificationTypeEnum.Error, 'error when unban user'));
      }
    } finally {
      if (loadingRequestId === loadingRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleImpersonateUser = async () => {
    const userId = selectedUserId;
    setOpenImpersonateUserDialog(false);
    setIsLoading(true);
    try {
      const data = await adminProvider.impersonate(userId!);
      if (data.chatAuthToken && data.token) {
        await chatProvider.logout();
        await chatProvider.login(data.chatAuthToken);
        sessionProvider.initAuthSession(data.token);
        window.location.href = '/';
      } else {
        dispatch(appSetNotification(NotificationTypeEnum.Error, data.message));
      }
    } catch (error) {
      dispatch(appSetNotification(NotificationTypeEnum.Error, 'error when impersonate user'));
    }
    setIsLoading(false);
  };

  const handleEmploymentRequest = async () => {
    const loadingRequestId = ++loadingRequestIdRef.current;
    setIsLoading(true);

    try {
      await getUserList();
    } finally {
      if (loadingRequestId === loadingRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleForeignBrokerVerification = async () => {
    setOpenForeignBrokerVerificationDialog(false);
    const loadingRequestId = ++loadingRequestIdRef.current;
    setIsLoading(true);

    try {
      await adminProvider.verifyUser(selectedUserId!);
      await Promise.all([getUserList(), getNonVerifiedUserList()]);
    } finally {
      if (loadingRequestId === loadingRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleAmbassadorStatusChange = async (isAmbassador: boolean) => {
    if (selectedUserId === undefined) {
      return;
    }

    const loadingRequestId = ++loadingRequestIdRef.current;
    setIsLoading(true);

    try {
      const { ok, status, message } = await adminProvider.setAmbassador(
        selectedUserId,
        isAmbassador
      );

      if (!ok) {
        dispatch(
          appSetNotification(
            NotificationTypeEnum.Error,
            message || 'Error when updating ambassador status',
            status
          )
        );
        return;
      }

      const [verifiedResult, verificationResult] = await Promise.all([
        getUserList(),
        getNonVerifiedUserList(),
      ]);
      const activeResult = tabValue === 0 ? verifiedResult : verificationResult;
      const currentPage = Math.floor(offset / limit) + 1;

      if (
        (tabValue === 0 || tabValue === 1) &&
        ambassadorFilter !== AmbassadorFilterValue.All &&
        activeResult &&
        activeResult.items.length === 0 &&
        currentPage > 1
      ) {
        await handleChangePage(currentPage - 1);
      }
    } catch {
      dispatch(
        appSetNotification(NotificationTypeEnum.Error, 'Error when updating ambassador status')
      );
    } finally {
      if (loadingRequestId === loadingRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const loadingRequestId = ++loadingRequestIdRef.current;
    setIsLoading(true);
    const isInitialLoad = isInitialUserListLoadRef.current;
    isInitialUserListLoadRef.current = false;
    const page = isInitialLoad && queryParams.page ? parseInt(queryParams.page as string) : 1;
    const newOffset = (page - 1) * limit;

    setOffset(newOffset);
    if (!isInitialLoad) {
      changeQueryParams({ page: 1 });
    }

    const loadUserLists = async () => {
      try {
        await Promise.all([
          getUserList(limit, newOffset),
          getNonVerifiedUserList(limit, newOffset),
          getUnregisteredUserList(limit, newOffset),
        ]);
      } finally {
        if (loadingRequestId === loadingRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadUserLists();

    return () => {
      userListRequestIdRef.current += 1;
      nonVerifiedUserListRequestIdRef.current += 1;
      unregisteredUserListRequestIdRef.current += 1;
    };
  }, [search, type, ambassadorFilter]);

  const handleClickOpenDialog = (dialogType: HeaderDialogsEnum) => {
    setOpenDialog((prevState) => ({
      ...prevState,
      [dialogType]: !prevState[dialogType],
    }));
  };

  const handleAddNewUser = () => {
    handleClickOpenDialog(HeaderDialogsEnum.SignUp);
    // handleClickOpenDialog(HeaderDialogsEnum.SelectUserType);
  };

  const selectedUser =
    tabValue === 0
      ? users.find((user: User) => user.id === selectedUserId)
      : nonVerifiedUsers.find((user: User) => user.id === selectedUserId);
  return (
    <>
      {isLoading && <Loader />}
      {!isLoading && (
        <Box
          data-qa='admin-users-layout'
          sx={{
            boxSizing: 'border-box',
            minWidth: 0,
            width: '100%',
            '& [data-qa="admin-users-search-toolbar"]': {
              boxSizing: 'border-box',
              minWidth: 0,
              width: '100%',
            },
            '& .MuiTableContainer-root': {
              boxSizing: 'border-box',
              maxWidth: 'none',
              minWidth: 0,
              overflowX: 'auto',
              width: '100%',
            },
            '& .MuiTable-root': {
              tableLayout: 'auto',
              width: '100%',
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              typography: 'body1',
              justifyContent: 'flex-start',
            }}
          >
            <StyledTabs value={tabValue} onChange={handleTabChange} centered>
              <StyledTab label={`Verified users (${userTotal})`} />
              <StyledTab label={`Verification requests (${nonVerifiedUserTotal})`} />
              <StyledTab label={`Unregistered users (${unregistredUsersTotal})`} />
              <StyledTab label='OTP codes' />
            </StyledTabs>
          </Box>
          {tabValue !== 2 && (
            <UserSearch
              search={search}
              tabValue={tabValue}
              typeUsers={type}
              ambassadorFilter={ambassadorFilter}
              setTypeUsers={setType}
              setAmbassadorFilter={setAmbassadorFilter}
              setSearch={setSearch}
              onAddNewUser={handleAddNewUser}
              setUpdateOtpTable={setUpdateOtpTable}
            />
          )}
          {tabValue === 0 && (
            <>
              <UserTable total={userTotal} users={users} handleMenuClick={handleMenuClick} />
              {paginationObj?.total && paginationObj?.total > 0 ? (
                <Pagination paginationObj={paginationObj} onChangePage={handleChangePage} />
              ) : null}
            </>
          )}
          {tabValue === 1 && (
            <>
              <VerificationRequestsTable
                total={nonVerifiedUserTotal}
                users={nonVerifiedUsers}
                handleMenuClick={handleMenuClick}
              />
              {nonVerifiedUsersPaginationObj?.total && nonVerifiedUsersPaginationObj?.total > 0 ? (
                <Pagination
                  paginationObj={nonVerifiedUsersPaginationObj}
                  onChangePage={handleChangePage}
                />
              ) : null}
            </>
          )}
          {tabValue === 2 && (
            <>
              <UnregisteredTable
                users={unregistredUsers}
                onDeleteUser={setUnregisteredUserToDelete}
              />
              {unregistredUsersPaginationObj?.total && unregistredUsersPaginationObj?.total > 0 ? (
                <Pagination
                  paginationObj={unregistredUsersPaginationObj}
                  onChangePage={handleChangePage}
                />
              ) : null}
            </>
          )}
          {tabValue === 3 && (
            <OtpTable
              search={search}
              setUpdateOtpTable={setUpdateOtpTable}
              updateOtpTable={updateOtpTable}
            />
          )}
          <UserMenu
            anchorEl={anchorEl}
            anchorPosition={anchorPosition}
            chatId={users.find((user: User) => user.id === selectedUserId)?.supportChatId}
            isBlocked={selectedUser?.status === 'blocked'}
            isAmbassador={selectedUser?.isAmbassador}
            userKind={selectedUser?.kind}
            userId={selectedUser ? String(selectedUser.id) : undefined}
            isVerificationEnabled={
              selectedUser?.kind === 'foreign' && selectedUser?.verified === false
            }
            handleMenuClose={handleMenuClose}
            handleBlockUserDialog={() => {
              setOpenBlockUserDialog(true);
            }}
            handleUnBlockUserDialog={() => {
              setOpenUnBlockUserDialog(true);
            }}
            handleBlockHistoryDialog={() => {
              setOpenBlockHistoryDialog(true);
            }}
            handleImpersonateUserDialog={() => {
              setOpenImpersonateUserDialog(true);
            }}
            handleEmploymentRequestDialog={() => {
              setOpenEmploymentRequestDialog(true);
            }}
            handleVerifyUserDialog={() => {
              setOpenForeignBrokerVerificationDialog(true);
            }}
            handleAmbassadorStatusChange={handleAmbassadorStatusChange}
          />
          <BlockUser
            open={openBlockUserDialog}
            userName={users.find((user) => user.id === selectedUserId)?.name ?? ''}
            onConfirm={handleBanUser}
            onClose={() => {
              setOpenBlockUserDialog(false);
            }}
          />
          <UnBlockUser
            open={openUnBlockUserDialog}
            userName={users.find((user) => user.id === selectedUserId)?.name ?? ''}
            onConfirm={handleUnBanUser}
            onClose={() => {
              setOpenUnBlockUserDialog(false);
            }}
          />
          <BlockHistory
            open={openBlockHistoryDialog}
            userId={selectedUserId!}
            onClose={() => setOpenBlockHistoryDialog(false)}
          />
          <ImpersonateUser
            open={openImpersonateUserDialog}
            userName={users.find((user) => user.id === selectedUserId)?.name ?? ''}
            onConfirm={handleImpersonateUser}
            onClose={() => {
              setOpenImpersonateUserDialog(false);
            }}
          />
          <EmploymentRequest
            open={openEmploymentRequestDialog}
            selectedUserId={selectedUserId!}
            onConfirm={handleEmploymentRequest}
            onClose={() => {
              setOpenEmploymentRequestDialog(false);
            }}
          />
          <ForeignBrokerVerification
            open={openForeignBrokerVerificationDialog}
            onConfirm={handleForeignBrokerVerification}
            onClose={() => {
              setOpenForeignBrokerVerificationDialog(false);
            }}
          />
        </Box>
      )}
      <MyDialog
        dialogTitle='Delete unregistered user?'
        onClose={closeDeleteUnregisteredUserDialog}
        open={unregisteredUserToDelete !== null}
        width='600'
      >
        <Stack spacing={3}>
          <Typography textAlign='center'>
            This will remove the unfinished registration and its verification codes.
          </Typography>
          <DialogActions sx={{ justifyContent: 'center', gap: 2 }}>
            <Button
              disabled={isDeletingUnregisteredUser}
              onClick={closeDeleteUnregisteredUserDialog}
              variant='outlined'
            >
              Cancel
            </Button>
            <Button
              color='error'
              disabled={isDeletingUnregisteredUser}
              onClick={() => void handleDeleteUnregisteredUser()}
              variant='contained'
            >
              {isDeletingUnregisteredUser ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Stack>
      </MyDialog>
    </>
  );
};
